
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { fetcher } from 'api/client';
import { useForm } from 'react-hook-form';
import { getMilestone, addMilestoneAttachment, markMilestoneAsCompleted, removeMilestoneAttachment } from 'api/actions/milestones';
import { getSession } from 'api/actions/auth'
import { fetchProfile } from 'api/actions/profile'
import DragZone from 'components/Shared/DragZone';
import { format, getMongoObjectId, parseDate } from 'utils';
import bootbox from 'components/Modals/Bootbox';
import { uploadFileV2 } from 'api/actions/file';
import { fetchMedicalPrescription, supplyMedicine } from 'api/actions/medical-prescriptions';
import { Card, Container, Row, Button, FormGroup, Modal, Label, Input, Collapse, FormFeedback, Spinner } from 'reactstrap';
import { useParams } from 'react-router-dom';
import useFileUpload from 'api/hooks/file/useFileUpload';
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup';

function DynamicLogo({ jwt, enabled }) {
    const { isLoading, data } = useQuery(['profile'], () => fetcher(fetchProfile(), jwt), {
        enabled,
    })
    if (isLoading) return <Spinner />

    return <img src={data?.tenant?.branding?.logo || "/images/owl-logo.svg"} className="w-10 mb-3" alt="Logo" width={200} />
}

function MedicalPrescriptions() {
    const { t, i18n } = useTranslation();
    const language = useRef();
    const queryClient = useQueryClient();
    const { medicalPrescriptionId, contractId, uuid } = useParams();
    const [jwt, setJWT] = useState();

    const { register, handleSubmit } = useForm();

    const { isLoading: isLoadingSession } = useQuery(['fetch-session'], () => fetcher(getSession({ uuid, contractId })), {
        enabled: !!uuid,
        onSuccess: (data) => {
            setJWT(data.jwt)
        }
    })

    useEffect(() => {
        // language.current.value = i18n.language;
    }, [i18n])

    const { data: medicalPrescription = {}, isSuccess, isLoading } = useQuery(['medical-prescription', medicalPrescriptionId], () => fetcher(fetchMedicalPrescription(medicalPrescriptionId), jwt), { 
        enabled: uuid ? !!medicalPrescriptionId && !!jwt : !!medicalPrescriptionId  
    })

    const handleLangChange = (e) => {
        i18n.changeLanguage(e.target.value)
    }

    return (
        <div className="">
            {/* <div className="d-flex justify-content-end">
                <FormGroup className="p-2" style={{width: 300}}>
                    <select onChange={handleLangChange} className="form-control" ref={language}>
                        <option value="en" >{t('profile.settings.lang_en')}</option>
                        <option value="es" >{t('profile.settings.lang_es')}</option>
                        <option value="pt" >{t('profile.settings.lang_pt')}</option>
                        <option value="de" >{t('profile.settings.lang_de')}</option>
                    </select>
                </FormGroup>
            </div> */}
            <Container className="p-5">
                <Card className="p-5">
                    <div className="d-flex align-items-center justify-content-center">
                        <DynamicLogo jwt={jwt} enabled={uuid ? !!jwt : true} />
                    </div>
                    {/* <img src="/images/owl-logo.svg" className="w-10 mb-3" alt="Trato Logo" height="100px" /> */}
                    
                    {
                        (isLoading || isLoadingSession) &&
                        <div className="mt-1 mb-1 mr-1 align-items-center justify-content rounded p-3 bg-grey file-container pointer">
                            <i className="fas fa-spinner fa-spin fa-2x background-orange p-2"></i>
                            <div className="d-flex flex-column ml-3">
                                <span>{t('milestones.loading')}...</span>
                            </div>
                        </div>
                    }
                    {isSuccess &&
                        <MedicineList medicalPrescription={medicalPrescription} jwt={jwt} />
                    }
                </Card>
            </Container>
        </div>
    )
}

export default MedicalPrescriptions;

const getSchema = (maxQuantity) => {
    return Yup.object().shape({
         pharmacy: Yup.string()
            .required('Requerido'),
        responsible: Yup.string(),
        quantity: Yup.number()
            .min(1, 'La minima cantidad a surtir es 1')
            .max(maxQuantity, `La maxima cantidad a surtir es ${maxQuantity}`)
            .required('Requerido'),
    });
}

function MedicineFormModal({ medicalPrescriptionId, contractId, isOpen, medicine = {}, jwt, toggle }) {
    const { t } = useTranslation();
    const maxQuantity = useMemo(() => medicine.isUnlimited ? 10000000000000000 : medicine.quantity - medicine.quantitySupplied, [medicine.isUnlimited, medicine.quantity, medicine.quantitySupplied]);
    const MedicineSupplySchema = useMemo(() => getSchema(maxQuantity), [maxQuantity])
    const { register, handleSubmit, errors } = useForm({
        resolver: yupResolver(MedicineSupplySchema)
    });
    const [attachments, setAttachments] = useState([]);
    const queryClient = useQueryClient();
    
    const onSuccess = () => {
        toggle()
        queryClient.invalidateQueries(['medical-prescription', medicalPrescriptionId]);
    }

    const { uploadFile, uploadFileState } = useFileUpload({
        jwt,
        onSuccess: (uploadResult, variables) => {
            const data = {
                _id: getMongoObjectId(),
                name: variables.name,
                url: uploadResult.url,
                file: uploadResult.fileId,
                createdAt: new Date()
            }
            setAttachments(attachments => ([
                ...attachments,
                data
            ]))
        }
    })

    const { mutate: mutateSupplyMedicine } = useMutation(({ medicalPrescriptionId, medicineId, ...payload}) => fetcher(supplyMedicine(medicalPrescriptionId, medicineId, payload), jwt), {
        onSuccess
    })

    const handleForm = (payload) => {
        console.log({payload})
        const data = {
            ...payload,
            medicalPrescriptionId,
            attachments,
            medicineId: medicine._id,
            quantity: Math.max(Math.min(Number(payload.quantity), maxQuantity), 1)
        }

        mutateSupplyMedicine(data)
    }

    const handleDeleteAttachment = async (attachmentId) => {
        if (await bootbox.confirm(t('milestones.confirm_delete'))) {
            setAttachments(attachments => attachments.filter(atachment => atachment._id !== attachmentId))
        }
    }

    const handleUpload = (files) => {
        files.forEach(async (file) => {
            const [fileName, ] = file.name.split(".");
            const name = await bootbox.prompt({
                text: t("agenda.ask_filename", { file_name: file.name }),
                inputText: fileName,
                buttons: {
                    confirm: {
                        color: 'primary'
                    }
                }
            });
            uploadFile({ file, name: name || fileName });
        })
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <form className="p-4" onSubmit={handleSubmit(handleForm)} noValidate>
                <h4 className="mb-4 text-center"><b>{medicine.name}</b></h4>
                <p><b>{t('Medicamento surtido')}:</b> {medicine?.supplyHistory?.length || 0} veces</p>
                <FormGroup className="m-0" inline>
                    <Label><b>{t('Nombre de la Farmacia')}</b></Label>
                    <Input 
                        bsSize="sm" 
                        type="text" 
                        id="pharmacy" 
                        name="pharmacy" 
                        innerRef={register} 
                        defaultValue={''} 
                        invalid={!!errors.pharmacy?.message}
                        // placeholder={t('milestones.name')} 
                    />
                    <FormFeedback invalid={!!errors.pharmacy?.message}>{errors.pharmacy?.message}</FormFeedback>
                </FormGroup>
                <FormGroup className="mt-1" inline>
                    <Label><b>{t('Nombre de la Persona que surte')}</b></Label>
                    <Input 
                        bsSize="sm" 
                        type="text" 
                        id="responsible" 
                        name="responsible" 
                        innerRef={register} 
                        defaultValue={''} 
                        invalid={!!errors.responsible?.message}
                        // placeholder={t('milestones.name')} 
                    />
                    <FormFeedback invalid={!!errors.responsible?.message}>{errors.responsible?.message}</FormFeedback>
                </FormGroup>
                <FormGroup className="mt-1" inline>
                    <Label><b>{t('Cantidad a surtir')}</b></Label>
                    <Input 
                        bsSize="sm" 
                        type="number" 
                        id="quantity" 
                        name="quantity" 
                        innerRef={register} 
                        defaultValue={1} 
                        min={1}
                        max={maxQuantity}
                        invalid={!!errors.quantity?.message}
                        // placeholder={t('milestones.name')} 
                    />
                    {errors.quantity?.message}
                     <FormFeedback invalid={!!errors.quantity?.message}>{errors.quantity?.message}</FormFeedback>
                </FormGroup>

                {attachments?.map((attachment) => (
                    <div key={attachment._id}>
                        <div className="float-right pointer remove-attachment" onClick={() => handleDeleteAttachment(attachment._id)}>
                            <i className="fas fa-times"></i>
                        </div>
                        <div className="d-flex mt-1 mb-1 mr-1 align-items-center justify-content rounded p-3 bg-grey file-container pointer" onClick={() => window.open(attachment.url, '_blank')}>
                            <i className="fas fa-file fa-2x background-orange p-2"></i>
                            <div className="d-flex flex-column ml-3">
                                <span>{attachment.name}</span>
                                <span className="text-muted">{format(attachment.createdAt, 'PPp')}</span>
                            </div>
                        </div>
                    </div>
                ))}
                {uploadFileState.isLoading &&
                    <div className="d-flex mt-1 mb-1 mr-1 align-items-center justify-content rounded p-3 bg-grey file-container pointer">
                        <i className="fas fa-spinner fa-spin fa-2x background-orange p-2"></i>
                        <div className="d-flex flex-column ml-3">
                            <span>{t('milestones.loading')}...</span>
                        </div>
                    </div>
                }
                <DragZone
                    multiple={true}
                    imgSrc="/images/listing/createDocument/subir.svg"
                    label={t('milestones.upload_evidences')}
                    className="frontdesk-drag mt-2"
                    acceptedFiles={["application/pdf", "image/*"]}
                    handleFile={handleUpload}
                    onDrop={handleUpload}
                />
                <div className="text-center mt-3"> 
                    <Button  size="lg" type="submit" color="primary">Confirmar</Button>
                </div>       
            </form>
        </Modal>
    )
}

function MedicineList({ medicalPrescription, jwt }) {
    const { t } = useTranslation();
    const  [currentMedicine, setCurrentMedicine] = useState({});

    const hadleSupply = (medicine) => {
        setCurrentMedicine(medicine)
    }

    return (
        <div>
            <h2 className="text-center font-weight-bold">
                {medicalPrescription.name}
            </h2>
            <p><b>{t('Documento')}:</b> {medicalPrescription.contract?.name}</p>
            {
                medicalPrescription?.medicines?.map(medicine => (
                    <MedicineItem medicine={medicine} onSupply={hadleSupply} />
                ))
            
            }
            <MedicineFormModal isOpen={currentMedicine._id} jwt={jwt} medicine={currentMedicine} medicalPrescriptionId={medicalPrescription._id} toggle={() => setCurrentMedicine({})} />
        </div>
    )
}

function MedicineItem({ medicine, onSupply }) {
    const [isOpen, setIsOpen] = useState(false)

    const handleSupply = () => {
        onSupply(medicine)
    }

    const toggle = () => setIsOpen(open => !open)

    return (
        <div className="bg-grey border rounded p-2">
        <div className="d-flex w-100 justify-content-between align-items-center" onClick={toggle}>
           
            <div>
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} mr-1`} />
                {medicine.name}
            </div>
            <div>
                {!medicine.isUnlimited && <span>{medicine.quantitySupplied} / {medicine.quantity}</span> }
                <Button className="ml-1" disabled={!medicine.isUnlimited && medicine.quantitySupplied === medicine.quantity} onClick={handleSupply}> {!medicine.isUnlimited && medicine.quantitySupplied === medicine.quantity ? "Surtido" : "Surtir"}</Button>
            </div>
        </div>
        <Collapse isOpen={isOpen}>
            <div className="font-weight-bold">Historial: </div>
                {
                    medicine.supplyHistory.map(history => (
                        <div className="p-2 border-bottom d-flex" key={history._id}>
                            <div className="text-muted">
                                {format(history.createdAt, 'PPp')}
                            </div>
                            <div className="font-weight-bold ml-2">
                               {history.pharmacy} surtió {history.quantity} medicamento(s)
                            </div>
                        </div>
                    ))
                }
                {
                   !medicine.supplyHistory.length &&
                    <div className="p-2 d-flex">
                        Este medicamento no ha sido surtido
                    </div>
                }
            </Collapse>
            </div>
    )
}